import { Close } from '@mui/icons-material';
import { Box, Grid, IconButton, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { generateBooleanQuery, IBooleanQueryBuilderDTO, IJob, amplitude } from 'wisestep-api';
import {
  theme,
  WsButton,
  WsColors,
  WsIconActiveJob,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../context';
import useCopyToClipboard from '../../../../candidates/talentpool/hooks/useCopyToClipboard';
import BooleanQryCard from '../booleanQueryCard';

interface GenerateBooleanQueryProps {
  closeDialog: () => void;
  handleClickOpen: () => void;
  job: IJob;
  defaultQuery: IBooleanQueryBuilderDTO;
}
const CardTheme = styled(Grid)(() => ({
  borderRadius: theme.spacing(0.5),
  backgroundColor: WsColors.TertiaryShades50,
  padding: theme.spacing(1),
}));

const GenerateBooleanQuery = ({
  closeDialog,
  handleClickOpen,
  job,
  defaultQuery,
}: GenerateBooleanQueryProps) => {
  const [, copy] = useCopyToClipboard();
  const workspace = useWorkspaceContext();
  const [query] = useState<IBooleanQueryBuilderDTO>(defaultQuery);
  const [openBooleanQuery, setOpenBooleanQuery] = useState<string>();
  const [focusedBooleanQuery, setFocusedBooleanQuery] = useState<string>();
  const [comprehensiveBooleanQuery, setComprehensiveBooleanQuery] = useState<string>();

  useEffect(() => {
    const getGeneratedBooleanQuery = async () => {
      if (query) {
        const queryResponse = await generateBooleanQuery(query);
        setOpenBooleanQuery(queryResponse.data.booleanQuery.open);
        setFocusedBooleanQuery(queryResponse.data.booleanQuery.focused);
        setComprehensiveBooleanQuery(queryResponse.data.booleanQuery.comprehensive);
      }
    };
    getGeneratedBooleanQuery();
  }, [query]);

  const booleanAmplitudeEvents = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'JobListing',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });

  return (
    <Box>
      <CardTheme item container xs={12} justifyItems="center">
        <Grid xs={6} pt={0.5}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
            Generate Boolean Query
          </WsTypography>
        </Grid>
        <Grid item xs={6} container justifyContent="flex-end">
          <Box pt={0.5} px={0.5}>
            <WsIconActiveJob />
          </Box>
          {job.title && (
            <Box pt={0.3}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                {job.title}
              </WsTypography>
            </Box>
          )}
          {job.jobCode && (
            <Box pt={0.9} px={0.5}>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                {job.jobCode}
              </WsTypography>
            </Box>
          )}
          {job.companyName && (
            <Box pt={0.4} px={0.5}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
                {` @ ${job.companyName}`}
              </WsTypography>
            </Box>
          )}
          <IconButton onClick={closeDialog}>
            <Close />
          </IconButton>
        </Grid>
      </CardTheme>

      <Box py={2}>
        {openBooleanQuery && (
          <Box mb={2}>
            <BooleanQryCard
              title="Open"
              description={openBooleanQuery}
              onclick={() => {
                booleanAmplitudeEvents('Job_Listing_job_card_AIboolean_open', 'Open (AI Boolean)');
                copy(openBooleanQuery);
              }}
            />
          </Box>
        )}
        {focusedBooleanQuery && (
          <Box mb={2}>
            <BooleanQryCard
              title="Focused"
              description={focusedBooleanQuery}
              onclick={() => {
                booleanAmplitudeEvents(
                  'Job_Listing_job_card_AIboolean_Focused',
                  'Focused (AI Boolean)'
                );
                copy(focusedBooleanQuery);
              }}
            />
          </Box>
        )}
        {comprehensiveBooleanQuery && (
          <Box mb={2}>
            <BooleanQryCard
              title="Comprehensive"
              description={comprehensiveBooleanQuery}
              onclick={() => {
                booleanAmplitudeEvents(
                  'Job_Listing_job_card_AIboolean_Comprehensive',
                  'Comprehensive (AI Boolean)'
                );
                copy(comprehensiveBooleanQuery);
              }}
            />
          </Box>
        )}
      </Box>

      <Grid container xs={12} justifyContent="flex-end" py={2}>
        <WsButton
          variant="contained"
          size="large"
          onClick={() => {
            booleanAmplitudeEvents(
              'Job_Listing_job_card_AIboolean_Customize',
              'Customize (AI Boolean)'
            );
            handleClickOpen();
          }}
        >
          Customize
        </WsButton>
      </Grid>
    </Box>
  );
};
export default GenerateBooleanQuery;
